import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { saveAuth, rememberArea } from "@/lib/api";

/**
 * After the mobile number is verified, the server says where it may go: a
 * "destination" is one account the number has (Super Admin; or Tenant Admin,
 * Seller or Customer of one tenant). These helpers turn the destination the
 * server opened into the right saved session and the right screen. The role
 * and tenant always come from the server's answer, never from the address bar.
 */
const AREA = { tenant_admin: "admin", seller: "seller", customer: "customer" };

/** Where a destination's session is kept (see authScope in api.js). */
export const destinationScope = (d) => (d.role === "super_admin" ? "super" : `t:${d.tenant_slug}:${AREA[d.role]}`);

/** The first screen of a destination. */
export const destinationPath = (d) => {
  if (d.role === "super_admin") return "/super";
  return `/t/${d.tenant_slug}${d.role === "customer" ? "" : `/${AREA[d.role]}`}`;
};

/**
 * Returns open(session, { next }): saves the session the server opened and
 * goes to its area. `next` is used only for a customer, and only inside that
 * customer's own tenant.
 */
export function useOpenDestination() {
  const navigate = useNavigate();
  return useCallback((session, { next } = {}) => {
    const d = session.destination;
    const home = destinationPath(d);
    saveAuth({ token: session.token, user: session.user }, destinationScope(d));
    rememberArea(home);
    const inside = d.role === "customer" && next && (next === home || next.startsWith(`${home}/`));
    // No "signed in" message: the area that opens says it, and on a phone a
    // message at the top would sit over that area's menu button.
    navigate(inside ? next : home, { replace: true });
  }, [navigate]);
}
